"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.measurePreviousPeriodDateDataSets = exports.measurePopAttribute = exports.measureFilters = exports.measureAggregation = exports.isMeasureFormatInPercent = exports.measureFormat = exports.measureTitle = exports.measureAlias = exports.measureArithmeticOperator = exports.measureArithmeticOperands = exports.measureMasterIdentifier = exports.measureDoesComputeRatio = exports.measureItem = exports.measureIdentifier = exports.measureUri = exports.measureLocalId = exports.isArithmeticMeasureDefinition = exports.isPreviousPeriodMeasureDefinition = exports.isPoPMeasureDefinition = exports.isMeasureDefinition = exports.isArithmeticMeasure = exports.isPreviousPeriodMeasure = exports.isPoPMeasure = exports.isAdhocMeasure = exports.isSimpleMeasure = exports.isMeasure = exports.idMatchMeasure = exports.anyMeasure = void 0;
var tslib_1 = require("tslib");
// (C) 2019-2021 GoodData Corporation
var isEmpty_1 = tslib_1.__importDefault(require("lodash/isEmpty"));
var ts_invariant_1 = tslib_1.__importDefault(require("ts-invariant"));
var objRef_1 = require("../../objRef");
/**
 * Implementation of measure predicate which always returns true.
 *
 * @public
 */
exports.anyMeasure = function (_) { return true; };
/**
 * Factory function for measure predicate which evaluates true for measures that match particular ID.
 *
 * @public
 */
exports.idMatchMeasure = function (id) { return function (m) {
    return m.measure.localIdentifier === id;
}; };
//
// Type guards
//
/**
 * Type guard for checking whether object is any type of measure.
 *
 * @public
 */
function isMeasure(obj) {
    return !isEmpty_1.default(obj) && obj.measure !== undefined;
}
exports.isMeasure = isMeasure;
/**
 * Type guard for checking whether object is a simple measure.
 *
 * @public
 */
function isSimpleMeasure(obj) {
    return isMeasure(obj) && isMeasureDefinition(obj.measure.definition);
}
exports.isSimpleMeasure = isSimpleMeasure;
/**
 * Type guard for checking whether object is an adhoc measure.
 * An adhoc measure is a measure having an aggregation, one or some filters or a computeRatio of true
 *
 * @public
 */
function isAdhocMeasure(obj) {
    if (!isSimpleMeasure(obj)) {
        return false;
    }
    var measureDefinition = obj.measure.definition.measureDefinition;
    return (!!measureDefinition.aggregation ||
        !!measureDefinition.computeRatio ||
        (Array.isArray(measureDefinition.filters) && measureDefinition.filters.length > 0));
}
exports.isAdhocMeasure = isAdhocMeasure;
/**
 * Type guard for checking whether object is a period-over-period measure.
 *
 * @public
 */
function isPoPMeasure(obj) {
    return isMeasure(obj) && isPoPMeasureDefinition(obj.measure.definition);
}
exports.isPoPMeasure = isPoPMeasure;
/**
 * Type guard for checking whether object is a previous-period measure.
 *
 * @public
 */
function isPreviousPeriodMeasure(obj) {
    return isMeasure(obj) && isPreviousPeriodMeasureDefinition(obj.measure.definition);
}
exports.isPreviousPeriodMeasure = isPreviousPeriodMeasure;
/**
 * Type guard for checking whether object is an arithmetic measure.
 *
 * @public
 */
function isArithmeticMeasure(obj) {
    return isMeasure(obj) && isArithmeticMeasureDefinition(obj.measure.definition);
}
exports.isArithmeticMeasure = isArithmeticMeasure;
/**
 * Type guard for checking whether object is a measure definition.
 *
 * @public
 */
function isMeasureDefinition(obj) {
    return !isEmpty_1.default(obj) && obj.measureDefinition !== undefined;
}
exports.isMeasureDefinition = isMeasureDefinition;
/**
 * Type guard for checking whether object is a period-over-period measure definition.
 *
 * @public
 */
function isPoPMeasureDefinition(obj) {
    return !isEmpty_1.default(obj) && obj.popMeasureDefinition !== undefined;
}
exports.isPoPMeasureDefinition = isPoPMeasureDefinition;
/**
 * Type guard for checking whether object is a previous period measure definition.
 *
 * @public
 */
function isPreviousPeriodMeasureDefinition(obj) {
    return !isEmpty_1.default(obj) && obj.previousPeriodMeasure !== undefined;
}
exports.isPreviousPeriodMeasureDefinition = isPreviousPeriodMeasureDefinition;
/**
 * Type guard for checking whether object is an arithmetic measure definition.
 *
 * @public
 */
function isArithmeticMeasureDefinition(obj) {
    return !isEmpty_1.default(obj) && obj.arithmeticMeasure !== undefined;
}
exports.isArithmeticMeasureDefinition = isArithmeticMeasureDefinition;
//
// Functions
//
/**
 * Gets measure's local identifier. For convenience and fluency, this function accepts both measure object and identifier
 * object.
 *
 * @param measureOrLocalId - measure object or measure localId; if localId provided, it is returned as is
 * @returns string identifier
 * @public
 */
function measureLocalId(measureOrLocalId) {
    ts_invariant_1.default(measureOrLocalId, "measure or local id must be specified");
    return typeof measureOrLocalId === "string" ? measureOrLocalId : measureOrLocalId.measure.localIdentifier;
}
exports.measureLocalId = measureLocalId;
/**
 * Gets URI of persistent measure; undefined is returned if the measure definition is not for a persistent
 * measure (arithmetic or derived). Undefined is returned if the measure is not specified by URI.
 *
 * @param measure - measure to get URI for
 * @returns URI or undefined
 * @public
 */
function measureUri(measure) {
    ts_invariant_1.default(measure, "measure must be specified");
    var ref = measureItem(measure);
    if (!ref) {
        return undefined;
    }
    return objRef_1.isUriRef(ref) ? ref.uri : undefined;
}
exports.measureUri = measureUri;
/**
 * Gets identifier of persistent measure; undefined is returned if the measure definition is not for a persistent
 * measure (arithmetic or derived). Undefined is returned if the measure is not specified by identifier.
 *
 * @param measure - measure to get URI for
 * @returns identifier or undefined
 * @public
 */
function measureIdentifier(measure) {
    ts_invariant_1.default(measure, "measure must be specified");
    var ref = measureItem(measure);
    if (!ref) {
        return undefined;
    }
    return objRef_1.isIdentifierRef(ref) ? ref.identifier : undefined;
}
exports.measureIdentifier = measureIdentifier;
function measureItem(measure) {
    var _a;
    ts_invariant_1.default(measure, "measure must be specified");
    return (_a = measure.measure.definition.measureDefinition) === null || _a === void 0 ? void 0 : _a.item;
}
exports.measureItem = measureItem;
/**
 * Tests whether the measure is set to compute ratio.
 *
 * @param measure - measure to to test
 * @returns true if computes ratio, false otherwise
 * @public
 */
function measureDoesComputeRatio(measure) {
    ts_invariant_1.default(measure, "measure must be specified");
    if (!isSimpleMeasure(measure)) {
        return false;
    }
    return !!measure.measure.definition.measureDefinition.computeRatio;
}
exports.measureDoesComputeRatio = measureDoesComputeRatio;
function measureMasterIdentifier(measure) {
    ts_invariant_1.default(measure, "measure must be specified");
    if (isPoPMeasure(measure)) {
        return measure.measure.definition.popMeasureDefinition.measureIdentifier;
    }
    else if (isPreviousPeriodMeasure(measure)) {
        return measure.measure.definition.previousPeriodMeasure.measureIdentifier;
    }
    return undefined;
}
exports.measureMasterIdentifier = measureMasterIdentifier;
function measureArithmeticOperands(measure) {
    ts_invariant_1.default(measure, "measure must be specified");
    if (!isArithmeticMeasure(measure)) {
        return undefined;
    }
    return measure.measure.definition.arithmeticMeasure.measureIdentifiers;
}
exports.measureArithmeticOperands = measureArithmeticOperands;
function measureArithmeticOperator(measure) {
    ts_invariant_1.default(measure, "measure must be specified");
    if (!isArithmeticMeasure(measure)) {
        return undefined;
    }
    return measure.measure.definition.arithmeticMeasure.operator;
}
exports.measureArithmeticOperator = measureArithmeticOperator;
/**
 * Gets measure alias.
 *
 * @param measure - measure to get the alias of
 * @returns measure alias if specified, undefined otherwise
 * @public
 */
function measureAlias(measure) {
    ts_invariant_1.default(measure, "measure must be specified");
    return measure.measure.alias;
}
exports.measureAlias = measureAlias;
/**
 * Gets measure title.
 * @param measure - measure to get the title of
 * @returns measure title if specified, undefined otherwise
 * @public
 */
function measureTitle(measure) {
    ts_invariant_1.default(measure, "measure must be specified");
    return measure.measure.title;
}
exports.measureTitle = measureTitle;
/**
 * Gets measure format.
 * @param measure - measure to get the format of
 * @returns measure format if specified, undefined otherwise
 * @public
 */
function measureFormat(measure) {
    ts_invariant_1.default(measure, "measure must be specified");
    return measure.measure.format;
}
exports.measureFormat = measureFormat;
/**
 * Gets a flag indicating whether a given measure has a format resulting in data being formatted as percentage
 * @param measureOrFormat - measure or measure format to test
 * @returns true if the measure format is in percent, false otherwise
 * @public
 * @remarks Measure format is considered to represent value in percent when
 * A) format string has no conditional separators (i.e. no semicolons except a single one at the end);
 *    otherwise the parsing would need access to a particular value.
 * B) percentage symbol is found (not directly preceded by backslash)
 */
function isMeasureFormatInPercent(measureOrFormat) {
    var format = isMeasure(measureOrFormat) ? measureFormat(measureOrFormat) : measureOrFormat;
    return !!format && /^([^;]*)%([^;]*)[;]*$/.test(format.trim().replace(/\\%/g, ""));
}
exports.isMeasureFormatInPercent = isMeasureFormatInPercent;
/**
 * Gets measure aggregation from a measure. Measure aggregation is applicable and optional only for
 * simple measures. Passing any other measure to this function guarantees that undefined will be returned
 *
 * @param measure - measure to get the aggregation of
 * @returns measure aggregation if specified, undefined otherwise
 * @public
 */
function measureAggregation(measure) {
    ts_invariant_1.default(measure, "measure must be specified");
    if (!isSimpleMeasure(measure)) {
        return undefined;
    }
    return measure.measure.definition.measureDefinition.aggregation;
}
exports.measureAggregation = measureAggregation;
/**
 * Gets measure filters.
 *
 * @param measure - measure to get the filters of
 * @returns measure filters if specified, undefined otherwise
 * @public
 */
function measureFilters(measure) {
    ts_invariant_1.default(measure, "measure must be specified");
    if (!isSimpleMeasure(measure)) {
        return undefined;
    }
    return measure.measure.definition.measureDefinition.filters;
}
exports.measureFilters = measureFilters;
function measurePopAttribute(measure) {
    ts_invariant_1.default(measure, "measure must be specified");
    if (!isPoPMeasure(measure)) {
        return undefined;
    }
    return measure.measure.definition.popMeasureDefinition.popAttribute;
}
exports.measurePopAttribute = measurePopAttribute;
function measurePreviousPeriodDateDataSets(measure) {
    ts_invariant_1.default(measure, "measure must be specified");
    if (!isPreviousPeriodMeasure(measure)) {
        return undefined;
    }
    return measure.measure.definition.previousPeriodMeasure.dateDataSets;
}
exports.measurePreviousPeriodDateDataSets = measurePreviousPeriodDateDataSets;
//# sourceMappingURL=index.js.map